import { FormHeader } from "app/components/atoms/layout";
import { Badge } from "app/components/atoms/badge";
import Table from "app/components/atoms/table";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";

const breadcrumbItems = [
  { label: "Report", href: "#" },
  { label: "Keluar-Masuk Gudang Utama", href: "#" },
];

// Kolom angka, urut sesuai rumus: Stok Awal + Masuk − Keluar = Sisa Stok.
const qtyColumns = [
  { key: "opening", label: "Stok Awal" },
  { key: "qty_in", label: "Masuk" },
  { key: "qty_out", label: "Keluar" },
  { key: "closing", label: "Sisa Stok" },
];

// Label jenis — hanya dipakai di sheet Rincian pada export Excel.
const LABEL_JENIS = {
  po_receive: "Terima PO",
  retur_customer: "Retur Customer",
  transfer_canceled: "Transfer Batal",
  so_delivery: "Kirim SO",
  transfer_out: "Transfer ke Outlet",
  pos_sale: "Penjualan POS",
  scrap: "Scrap",
  retur_supplier: "Retur Supplier",
};

const formatTanggal = (iso) =>
  new Date(iso).toLocaleString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const adaSelisih = (r) => r.selisih != null && r.selisih !== 0;
const tandaSelisih = (n) => (n > 0 ? `+${n}` : `${n}`);

/**
 * Keluar-masuk barang Gudang Utama per varian, dengan stok awal dan sisa yang
 * dihitung dari dokumen (lihat `StoreReportService.warehouseInOut`). Varian
 * yang qty master-nya tidak cocok dengan dokumen diberi tanda Selisih;
 * rincian per jenis dan nomor dokumen ada di export Excel.
 */
function StoreReportWarehouseInOutPage() {
  const [queryParams, setQueryParams] = useState(defaultReportDates());

  const [data, , loader, , loadError] = useFetcher({
    modelName: "storeReport",
    methodName: "warehouseInOut",
    payload: { id: queryParams },
  });
  const rows = Array.isArray(data?.summary) ? data.summary : [];
  const rincian = Array.isArray(data?.details) ? data.details : [];
  const totals = qtyColumns.reduce((acc, { key }) => {
    acc[key] = rows.reduce((sum, r) => sum + Number(r[key] ?? 0), 0);
    return acc;
  }, {});
  const jumlahSelisih = rows.filter(adaSelisih).length;

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const rekapSheet = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        "Kode Produk": r.product_code,
        Produk: r.product_name,
        Size: r.size_name ?? "-",
        Warna: r.color_name ?? "-",
        ...Object.fromEntries(qtyColumns.map((c) => [c.label, Number(r[c.key] ?? 0)])),
        Selisih: adaSelisih(r) ? r.selisih : "",
      }))
    );
    rekapSheet["!cols"] = [
      { wch: 16 }, { wch: 30 }, { wch: 10 }, { wch: 14 },
      ...qtyColumns.map(() => ({ wch: 10 })),
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(workbook, rekapSheet, "Rekap");

    const rincianSheet = XLSX.utils.json_to_sheet(
      rincian.map((r) => ({
        Tanggal: formatTanggal(r.tanggal),
        Jenis: LABEL_JENIS[r.type] ?? r.type,
        "No. Dokumen": r.doc_code || "-",
        "Kode Produk": r.product_code,
        Produk: r.product_name,
        Size: r.size_name ?? "-",
        Warna: r.color_name ?? "-",
        Masuk: Number(r.qty_in ?? 0),
        Keluar: Number(r.qty_out ?? 0),
        Keterangan: r.note || "-",
      }))
    );
    rincianSheet["!cols"] = [
      { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 30 },
      { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 24 },
    ];
    XLSX.utils.book_append_sheet(workbook, rincianSheet, "Rincian");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `laporan_keluar_masuk_gudang_utama_${queryParams.start_date}_${queryParams.end_date}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="report"
      activeSidebarChild="warehouse-in-out"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Keluar-Masuk Gudang Utama"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="report-warehouse-in-out-date-range"
                label="Tanggal"
                from={queryParams.start_date}
                to={queryParams.end_date}
                onFromChange={(e) =>
                  setQueryParams({ ...queryParams, start_date: e.target.value })
                }
                onToChange={(e) =>
                  setQueryParams({ ...queryParams, end_date: e.target.value })
                }
              />
            </div>
          </FormHeader>
        </Table.SearchBarWrapper>
        <p className="text-xs px-2 pt-2 text-gray-500 font-light">
          Stok Awal + Masuk − Keluar = Sisa Stok, dihitung dari dokumen. Masuk:
          terima PO, retur customer, transfer batal. Keluar: kirim SO, transfer
          ke outlet, penjualan POS Gudang Utama, scrap, retur supplier. Rincian
          per dokumen tersedia di Export Excel.
        </p>
        {jumlahSelisih > 0 && (
          <p className="text-xs px-2 pt-2 text-danger-700" data-testid="report-warehouse-in-out-selisih-warning">
            {jumlahSelisih} varian bertanda Selisih: qty di master produk tidak
            cocok dengan dokumen (edit qty lewat Ubah Variant atau stock opname).
            Stok Awal dan Sisa Stok varian itu meleset sebesar angka selisihnya.
          </p>
        )}
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode Produk</Table.Heading>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              {qtyColumns.map((c) => (
                <Table.Heading key={c.key}>
                  <p className="text-right">{c.label}</p>
                </Table.Heading>
              ))}
              <Table.Heading>Cek</Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {rows.map((r) => (
              <Table.BodyRow key={r.product_variant_id}>
                <Table.Cell>{r.product_code}</Table.Cell>
                <Table.Cell>{r.product_name}</Table.Cell>
                <Table.Cell>{r.size_name ?? "-"}</Table.Cell>
                <Table.Cell>{r.color_name ?? "-"}</Table.Cell>
                {qtyColumns.map((c) => (
                  <Table.Cell key={c.key} fontWeight={c.key === "closing" ? "semibold" : undefined}>
                    <p className="text-right">{Number(r[c.key] ?? 0)}</p>
                  </Table.Cell>
                ))}
                <Table.Cell>
                  {adaSelisih(r) && (
                    <Badge color="red">Selisih {tandaSelisih(r.selisih)}</Badge>
                  )}
                </Table.Cell>
              </Table.BodyRow>
            ))}
            {rows.length > 0 && (
              <Table.BodyRow>
                <Table.Cell colSpan={4} fontWeight="semibold">Total</Table.Cell>
                {qtyColumns.map((c) => (
                  <Table.Cell key={c.key} fontWeight="semibold">
                    <p className="text-right">{totals[c.key]}</p>
                  </Table.Cell>
                ))}
                <Table.Cell />
              </Table.BodyRow>
            )}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">Memuat data...</p>
      )}
      {loadError && !loader.state && (
        <p className="text-sm px-2 py-1 text-red-600" data-testid="report-load-error">
          Gagal memuat data: {loadError}
        </p>
      )}
      {rows.length === 0 && !loader.state && !loadError && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportWarehouseInOutPage;
